import { createFileRoute, Link } from "@tanstack/react-router";
import { Printer } from "lucide-react";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { Panel, RiskBadge, VerdictBadge } from "@/lovable-demo/components/bits";
import { Button } from "@/components/ui/button";
import {
  portfolio,
  recordsForProgram,
  rpnDelta,
  topFive,
} from "@/lovable-demo/lib/warranty/aggregate";
import { recordApDelta } from "@/lovable-demo/lib/warranty/action-priority";
import {
  currentStage,
  fmtCompactCurrency,
  fmtCurrency,
  fmtDate,
  missingEvidence,
  recordDelayCost,
  recordVerdict,
} from "@/lovable-demo/lib/warranty/calc";
import { exportPdf } from "@/lovable-demo/lib/warranty/export";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";
import { DecisionsRequiringAttention } from "@/lovable-demo/components/escalation-summaries";

export const Route = createFileRoute("/demo/brief")({
  head: () => ({
    meta: [
      { title: "Warranty Governance Briefing — Broadview" },
      {
        name: "description",
        content:
          "Narrative briefing pack per Problem Record: position, evidence gate, root cause, financial exposure and read-across, ready for PDF export.",
      },
      { property: "og:title", content: "Warranty Governance Briefing — Broadview" },
      {
        property: "og:description",
        content:
          "Narrative briefing pack per Problem Record: position, evidence gate, root cause, financial exposure and read-across, ready for PDF export.",
      },
    ],
  }),
  component: Brief,
});

function Brief() {
  const { data, activeProgramId } = useWarranty();
  const records = recordsForProgram(data, activeProgramId);
  const program = data.programs.find((p) => p.id === activeProgramId);
  const stats = portfolio(records);
  const five = topFive(records);

  return (
    <AppShell
      title="Governance Briefing"
      subtitle={program ? `${program.code} · ${program.name}` : "All programs"}
      actions={
        <Button variant="outline" size="sm" onClick={exportPdf}>
          <Printer className="size-3.5" /> Export PDF
        </Button>
      }
    >
      <div className="space-y-4">
        <Panel title="Briefing header">
          <dl className="grid gap-4 sm:grid-cols-4">
            {[
              ["Prepared", fmtDate(new Date().toISOString())],
              ["Scope", program ? program.name : "Consolidated portfolio"],
              ["Records in scope", `${records.length} (${stats.open} open)`],
              ["Exposure", fmtCurrency(stats.exposure)],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="text-[0.62rem] uppercase tracking-wider text-muted-foreground">
                  {k}
                </dt>
                <dd className="num mt-1 text-sm font-semibold">{v}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-4 text-sm leading-relaxed text-foreground/90">
            This briefing covers the five highest-risk governed Problem Records. Each entry states
            the current stage, what is blocking the evidence gate, the proven or leading root cause,
            the financial position and the read-across obligation. Claim-level detail (VIN, dealer,
            mileage) sits in the Claims tab for signed-in users.
          </p>
        </Panel>

        <DecisionsRequiringAttention recordIds={records.map((r) => r.id)} />

        {five.map((r, i) => {
          const stage = currentStage(r.stages);
          const missing = missingEvidence(stage);
          const delta = rpnDelta(r);
          const openReadAcross = r.readAcross.filter((x) => x.status !== "cleared");
          return (
            <Panel
              key={r.id}
              title={`Brief ${i + 1} · ${r.code}`}
              subtitle={r.title}
              actions={
                <Link
                  to="/demo/problem-record/$id"
                  params={{ id: r.id }}
                  className="no-print text-xs text-primary hover:underline"
                >
                  Open record
                </Link>
              }
            >
              <div className="flex flex-wrap items-center gap-2">
                <RiskBadge risk={r.risk} score={r.riskScore} />
                <VerdictBadge verdict={recordVerdict(r.financial)} />
                <span className="num text-xs text-muted-foreground">
                  stage {stage.index}/11 — {stage.name}
                </span>
              </div>

              <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/90">
                <p>
                  <span className="font-semibold text-foreground">Position. </span>
                  {r.description}
                </p>
                <p>
                  <span className="font-semibold text-foreground">Gate. </span>
                  {missing.length === 0
                    ? `Stage ${stage.index} has its full evidence set and is ready for governance sign-off.`
                    : `Stage ${stage.index} is held: ${missing.join("; ")} outstanding. Owner ${stage.owner}, due ${fmtDate(stage.dueDate)}.`}
                </p>
                <p>
                  <span className="font-semibold text-foreground">Cause and control. </span>
                  {r.failureCause}. Highest Action Priority moves {recordApDelta(r.pfmea).before} →{" "}
                  {recordApDelta(r.pfmea).after} (RPN legacy {delta.before} → {delta.after},{" "}
                  {delta.reduction}% reduction) through{" "}
                  {r.pfmea[0]?.controlTechnique ?? "the revised control"}.
                </p>
                <p>
                  <span className="font-semibold text-foreground">Financial. </span>
                  {fmtCompactCurrency(r.financial.actualCost)} incurred against{" "}
                  {fmtCompactCurrency(r.financial.exposure)} exposure; delay, based on remaining
                  exposure, carries {fmtCompactCurrency(recordDelayCost(r))} per month. Target
                  reduction {r.financial.targetReductionPct}% versus{" "}
                  {r.financial.observedReductionPct}% observed.
                </p>
                <p>
                  <span className="font-semibold text-foreground">Read-across. </span>
                  {openReadAcross.length === 0
                    ? "All similar platforms screened and cleared."
                    : `${openReadAcross.length} platform(s) still open: ${openReadAcross
                        .map((x) => x.program)
                        .join(", ")}.`}
                </p>
              </div>
            </Panel>
          );
        })}
      </div>
    </AppShell>
  );
}
